/* global VERSION */
var annotatedFieldsWhiteList = require('./annotatedFieldsWhiteList');

/** 
 * @typedef {Object} SystemConfigurations
 * @property {string} client The client string to use when sending requests to services
 * @property {string} version The current build version
 * @property {LoggingSettings} logging
 * @property {string} elementPrefix The name prefix that should be used on any Songbird generated html element that has an html Id. This is to help prevent Id collisions on the merchants site
 * @property {PaymentsObject} payments
 * @property {string} defaultJWTAlgorithm The JWT signing algorithm to use when generating browser JWT's. Only used on a client JWT integration.
 * @property {WhiteListObject} whiteList
 * @property {number} paymentLoadTimeout The amount of time we should wait before considering a payment brand chunk a failure to load
 * @property {number} initTimeout The amount of time we should wait before considering the init to be slow. This will not throw an error, only log an error to enable remote logging. 
 * @property {number} defaultBinProfilingDelay The amount of time we should wait for bin profiling if an error occurred while attempting to pull the delay from DF
 * @property {boolean} enableEndpointMonitor A flag to enable/disable the EM data collection. This is helpful to reduce log clutter in logs while developing.
 * @property {FarnsworthLabsConfigurations} farnsworthLabs
 */

var systemConfigs = {
  client: 'SongbirdJS',
  version: VERSION,
  logging: {
    /**
     * A lot of logging configuration options can be found at: http://js.jsnlog.com/Documentation/JSNLogJs/AjaxAppender/SetOptions
     * @typedef {LoggingOptions}
     * @property {{verbose: number, debug: number, off: number}} levels The mapping of int levels used by JSNLogger and the text version of log levels
     * @property {{level: number}} console The default logging settings for the console log appender
     * @property {string} level The default logging level
     * @property {AjaxOptions} ajax The 
     */
    levels: {
      verbose: 0,
      debug: 3000,
      off: 5000
    },
    console: {
      level: 0
    },
    level: 'off',
    ajax: {
      /**
       * Log configurations defined at: http://js.jsnlog.com/Documentation/JSNLogJs/AjaxAppender/SetOptions
       * @typedef {AjaxOptions}
       * @property {boolean} enabled A flag to enable remote logging via Ajax requests
       * @property {number} level The numeric log level that should trigger a remote logging push
       * @property {number} bufferSize How many log statements should be kept in a buffer to send on a remote logging request with the log statement that triggered the event
       * @property {number} sendWithBufferLevel The numeric log level that will trigger a buffer dump to remote logs
       * @property {number} batchSize The minimal amount of logs needed to cause a log push once logging is enabled
       */
      enabled: true,
      level: 5000,
      bufferSize: 25,
      sendWithBufferLevel: 5000,
      batchSize: 10
    }
  },
  elementPrefix: 'Cardinal-',
  payments: {
    applePay: {
      name: 'Apple Pay',
      paymentBrandOptions: {
        start: {
          autoWired: false
        },
        autoShowPaymentButton: true,
        button: {
          id: 'ApplePay',
          elementType: 'div',
          whiteList: {
            color: [
              'white',
              'white-bordered',
              'black'
            ]
          }
        }
      }
    },
    cca: {
      name: 'ConsumerAuthentication',
      frameId: 'CCAFrame',
      termPath: {
        v2: 'TermURL/2.0/CCA'
      }
    },
    discoverWallet: {
      name: 'Discover Wallet',
      type: 'wallet',
      cookieDetectionPath: '/iframe/cookiedetection',
      paymentBrandOptions: {
        start: {
          required: true
        },
        autoShowPaymentButton: true,
        button: {
          id: 'DiscoverWallet',
          imagePath: 'https://d1nnphaioysusi.cloudfront.net/images/discover.button.png'
        },
      }
    },
    hostedFields: {
      name: 'HostedFields',
      minimumFields: [
        'AccountNumber'
      ]
    },
    paypal: {
      name: 'PayPal',
      braintree: {
        scripts: {
          client: 'https://js.braintreegateway.com/web/3.6.0/js/client.min.js',
          paypal: 'https://js.braintreegateway.com/web/3.6.0/js/paypal.min.js',
          dataCollection: 'https://js.braintreegateway.com/web/3.6.0/js/data-collector.min.js'
        }
      },
      paymentBrandOptions: {
        autoShowPaymentButton: true,
        button: {
          id: 'PayPal',
          imagePath: 'https://www.paypalobjects.com/webstatic/en_US/i/btn/png/',
          whiteList: {
            color: [
              'gold',
              'silver',
              'blue'
            ],
            shape: [
              'pill',
              'rect'
            ],
            size: [
              '26px',
              '34px',
              '44px',
              '60px'
            ],
            style: [
              'paypal',
              'paypalcheckout'
            ]
          }
        }
      }
    },
    visaCheckout: {
      name: 'Visa Checkout',
      paymentBrandOptions: {
        start: {
          require: false,
        },
        button: {
          id: 'Visa-Checkout',
          whiteList: {
            color: [
              'standard',
              'neutral'
            ],
            locale: null,
            size: [
              '154',
              '213',
              '425'
            ],
            height: [
              '34',
              '47',
              '94'
            ],
            width: null
          }
        }
      }
    }
  },
  defaultJWTAlgorithm: 'HS256',
  whiteList: {
    buttonConfigurations: [
      'containerId'
    ],
    annotatedFields: annotatedFieldsWhiteList
  },
  paymentLoadTimeout: 4000,
  initTimeout: 2000,
  defaultBinProfilingDelay: 2000,
  defaultMethodUrlDelay: 10000,
  deviceProfilingTimeout: 4000,
  enableEndpointMonitor: true,
  baseRequestTimeout: 6000,
  farnsworthLabs: {}
};

systemConfigs['button'] = {
  containerId: systemConfigs.elementPrefix + 'Payments'
};

/** @type {SystemConfigurations} */
module.exports = systemConfigs;


//////////////////
// WEBPACK FOOTER
// ./config/systemDefaultConfig.js
// module id = 29
// module chunks = 0 5 20